import { useState, useEffect, useRef, useCallback } from 'react';
import type { Dashboard as DashboardType, Panel, WidgetType, LabeledListFieldValue } from '@/types/dashboard';
import type { EntityListData } from '@/types/entity-list';
import { useDashboard } from '@/hooks/useDashboard';
import { loadPanelData } from '@/utils/api';
import { t } from '@/i18n';
import { GridContainer } from './GridContainer';
import { AddPanelModal } from './AddPanelModal';
import { PanelEditor } from './editor/PanelEditor';
import { DashboardSettings } from './DashboardSettings';
import type { SaveSettingsOptions } from './DashboardSettings';
import { saveConfig, deleteDashboard } from '@/utils/api';
import { cleanDashboardForSave } from '@/utils/persistence';
import { getDefaultPanelSize } from '@/utils/panelDefaults';
import { generateUUID } from '@/utils/uuid';

interface DashboardProps {
  initialConfig: Partial<DashboardType>;
  isEditable?: boolean;
  contextKey?: string;
  editingPanelId?: string;
  onDirtyChange?: (dirty: boolean) => void;
}

export function Dashboard({
  initialConfig,
  isEditable = false,
  contextKey = '',
  editingPanelId,
  onDirtyChange,
}: DashboardProps) {
  const {
    dashboard,
    isDirty,
    initialize,
    save,
    addPanel,
    removePanel,
    updatePanel,
    syncPanelPositions,
    markPanelLoaded,
    updateLayout,
    resetAllPanelData,
    reinitialize,
  } = useDashboard();

  const initializedRef = useRef(false);
  const openedEditingIdRef = useRef<string | null>(null);
  const prevContextKeyRef = useRef(contextKey);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingPanel, setEditingPanel] = useState<Panel | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [deletingPanelId, setDeletingPanelId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [conditionalFormError, setConditionalFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      initialize(initialConfig);
    }
  }, [initialConfig, initialize]);

  useEffect(() => {
    if (prevContextKeyRef.current && prevContextKeyRef.current !== contextKey && dashboard) {
      resetAllPanelData();
    }
    prevContextKeyRef.current = contextKey;
  }, [contextKey, dashboard, resetAllPanelData]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (
      editingPanelId
      && editingPanelId !== openedEditingIdRef.current
      && dashboard
    ) {
      const panel = dashboard.panels.find(p => p.id === editingPanelId);
      if (panel) {
        openedEditingIdRef.current = editingPanelId;
        setEditingPanel({ ...panel });
        setShowEditor(true);
      }
    }
  }, [editingPanelId, dashboard]);

  const handlePanelVisible = useCallback((panelId: string) => {
    if (!dashboard || !dashboard.uuid || !contextKey) return;

    const panel = dashboard.panels.find(p => p.id === panelId);
    if (!panel || panel._dataLoaded || panel._isLoading) return;

    // Chat widget manages its own data — skip backend panel data loading
    if (panel.type === 'chat') {
      markPanelLoaded(panelId, { _isLoading: false, _dataLoaded: true });
      return;
    }

    markPanelLoaded(panelId, { _isLoading: true });

    loadPanelData({
      dashboardUuid: dashboard.uuid,
      panelId,
      contextKey,
      panelType: panel.type,
      panelConfig: panel.config,
      formulas: panel.data?.formulas ?? {},
    })
      .then((response) => {
        const rawValues = response.data.values;

        if (panel.type === 'indicator') {
          const values = rawValues as Record<string, LabeledListFieldValue>;
          markPanelLoaded(panelId, {
            data: {
              ...panel.data,
              value: values.main?.value ?? null,
              delta: values.main?.delta,
            },
            _isLoading: false,
            _dataLoaded: true,
          });
        } else if (panel.type === 'labeled_list') {
          const values = rawValues as Record<string, LabeledListFieldValue>;
          markPanelLoaded(panelId, {
            data: {
              ...panel.data,
              values,
            },
            _isLoading: false,
            _dataLoaded: true,
          });
        } else if (panel.type === 'chart') {
          markPanelLoaded(panelId, {
            data: { ...panel.data, ...rawValues },
            _isLoading: false,
            _dataLoaded: true,
          });
        } else if (panel.type === 'chat') {
          // Chat widget manages its own data — no backend panel data needed
          markPanelLoaded(panelId, {
            _isLoading: false,
            _dataLoaded: true,
          });
        } else if (panel.type === 'entity_list') {
          const entityListData = rawValues as EntityListData;
          const items = entityListData.items ?? [];

          markPanelLoaded(panelId, {
            data: {
              ...panel.data,
              items,
              genericConfig: entityListData.genericConfig,
              meta: entityListData.meta ?? { totalCount: 0 },
            } as unknown as Panel['data'],
            _isLoading: false,
            _dataLoaded: true,
          });
        }
      })
      .catch((err) => {
        markPanelLoaded(panelId, {
          _isLoading: false,
          _dataLoaded: true,
          _loadError: err instanceof Error ? err.message : String(err),
        });
      });
  }, [dashboard, contextKey, markPanelLoaded]);

  if (!dashboard) {
    return null;
  }

  const handleSave = async () => {
    try {
      await save();
    } catch (err) {
      console.error('Dashboard save failed:', err);
    }
  };

  const handleSettingsSave = async (options: SaveSettingsOptions) => {
    if (!dashboard) return;

    if (options.updatedTitle !== undefined && options.updatedTitle !== dashboard.title) {
      const updated = { ...dashboard, title: options.updatedTitle };
      await saveConfig(cleanDashboardForSave(updated) as DashboardType);
      reinitialize(updated);
      setShowSettings(false);
      return;
    }

    if (options.updatedViewModeCondition !== undefined) {
      const updated = { ...dashboard, viewModeCondition: options.updatedViewModeCondition };
      try {
        await saveConfig(cleanDashboardForSave(updated) as DashboardType);
        reinitialize(updated);
        setShowSettings(false);
      } catch (err) {
        console.error('Failed to update viewModeCondition:', err);
      }
      return;
    }

    if (options.newViewMode && options.viewModeCondition) {
      const newUuid = generateUUID();
      const newDashboard: DashboardType = {
        uuid: newUuid,
        sourceKey: options.contextKey ? options.contextKey.split(':').slice(0, 2).join(':') : '',
        title: dashboard.title,
        layout: options.layout,
        viewMode: options.newViewMode,
        viewModeCondition: options.viewModeCondition,
        displayType: options.displayType ?? 'single',
        panels: dashboard.panels.map(p => ({ ...p })),
        contextKey: options.contextKey || '',
      };
      try {
        await saveConfig(cleanDashboardForSave(newDashboard) as DashboardType);
        reinitialize(newDashboard);
        setShowSettings(false);
        setConditionalFormError(null);
      } catch (err) {
        console.error('Failed to create conditional dashboard:', err);
        setConditionalFormError(err instanceof Error ? err.message : String(err));
      }
      return;
    }

    if (options.templateType === 'specific') {
      const newUuid = generateUUID();
      // Only inherit standard viewModes; custom ones (from conditional dashboards)
      // should not leak into specific copies — default to 'full'.
      const inheritedViewMode = (dashboard.viewMode === 'list' || dashboard.viewMode === 'full')
        ? dashboard.viewMode
        : 'full';
      const inheritedDisplayType = dashboard.displayType || (inheritedViewMode === 'list' ? 'list' : 'single');
      const newDashboard: DashboardType = {
        uuid: newUuid,
        sourceKey: options.contextKey || '',
        title: dashboard.title,
        layout: options.layout,
        viewMode: inheritedViewMode,
        displayType: inheritedDisplayType,
        panels: dashboard.panels.map(p => ({ ...p })),
        contextKey: options.contextKey || '',
      };
      try {
        await saveConfig(cleanDashboardForSave(newDashboard) as DashboardType);
        reinitialize(newDashboard);
        setShowSettings(false);
      } catch (err) {
        console.error('Failed to create specific dashboard:', err);
      }
    } else {
      const updated = { ...dashboard, layout: options.layout };
      try {
        await saveConfig(cleanDashboardForSave(updated) as DashboardType);
        updateLayout(options.layout);
        setShowSettings(false);
      } catch (err) {
        console.error('Failed to save dashboard settings:', err);
      }
    }
  };

  const handleDeleteSpecific = async () => {
    if (!dashboard?.uuid) return;
    try {
      await deleteDashboard(dashboard.uuid);
      window.location.reload();
    } catch (err) {
      console.error('Failed to delete dashboard:', err);
    }
  };

  const handleAddPanelSelect = (type: string) => {
    setShowAddPanel(false);
    addPanel(type as WidgetType, (newPanelId) => {
      const defaultSize = dashboard.layout.defaultPanelSizes?.[type as WidgetType]
                       ?? getDefaultPanelSize(type as WidgetType);
      setEditingPanel({
        id: newPanelId,
        type: type as WidgetType,
        x: 0, y: 0, w: defaultSize.w, h: defaultSize.h,
        config: {},
      });
      setShowEditor(true);
    });
  };

  const handleEditorApply = (updatedPanel: Panel) => {
    updatePanel(updatedPanel.id, updatedPanel);
    setShowEditor(false);
    setEditingPanel(null);
  };

  const handleEditorCancel = () => {
    setShowEditor(false);
    setEditingPanel(null);
  };

  const handleDeletePanel = (id: string) => {
    setDeletingPanelId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (deletingPanelId) {
      removePanel(deletingPanelId);
      if (editingPanel?.id === deletingPanelId) {
        setShowEditor(false);
        setEditingPanel(null);
      }
    }
    setShowDeleteConfirm(false);
    setDeletingPanelId(null);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletingPanelId(null);
  };

  const title = dashboard.title || t('dashboard.title');
  const isSpecific = (dashboard.sourceKey?.split(':').length ?? 0) === 3;
  const isListView = dashboard.displayType === 'list';

  return (
    <div className="dashboard-container">
      {isEditable && (
        <div
          className="card mb-3"
          data-testid="dashboard-toolbar"
        >
          <div className="card-body d-flex flex-column" style={{ gap: '0.35rem' }}>
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="card-title mb-0">{title}</h4>
              <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary"
                data-testid="dashboard-save-btn"
                disabled={!isDirty}
                onClick={handleSave}
              >
                <i className="las la-save"></i> {t('dashboard.save')}
              </button>
              <button
                type="button"
                className="btn btn-soft-secondary"
                data-testid="dashboard-settings-btn"
                aria-label={t('dashboard.settings')}
                onClick={() => setShowSettings(true)}
              >
                <i className="las la-cog"></i> {t('dashboard.settings')}
              </button>
              <button
                className="btn btn-primary"
                data-testid="dashboard-add-panel-btn"
                onClick={() => setShowAddPanel(true)}
              >
                <i className="las la-plus"></i> {t('dashboard.addPanel')}
              </button>
            </div>
          </div>
          <div className="d-flex justify-content-end align-items-center gap-2">
            <code className="small text-muted" data-testid="dashboard-sourcekey">{dashboard.sourceKey} · {dashboard.viewMode}</code>
            <span
              className={`badge ${isSpecific ? 'bg-warning' : 'bg-info'}`}
              data-testid="dashboard-template-badge"
              style={{ fontSize: '0.65rem' }}
            >
              {isSpecific ? t('dashboard.templateType.specific') : t('dashboard.templateType.generic')}
            </span>
          </div>
          </div>
        </div>
      )}

      <GridContainer
        layout={dashboard.layout}
        panels={dashboard.panels.map(p => ({
          ...p,
          contextKey: dashboard.contextKey || contextKey,
          currentUser: dashboard.currentUser,
        }))}
        onLayoutChange={(layout) => syncPanelPositions(layout)}
        onPanelVisible={handlePanelVisible}
        onEditPanel={(id) => {
          const panel = dashboard.panels.find(p => p.id === id);
          if (panel) {
            setEditingPanel({ ...panel });
            setShowEditor(true);
          }
        }}
        onDeletePanel={handleDeletePanel}
        onDuplicatePanel={(id) => {
          const panel = dashboard.panels.find(p => p.id === id);
          if (panel) {
            addPanel(panel.type as WidgetType, (newPanelId) => {
              updatePanel(newPanelId, {
                id: newPanelId,
                type: panel.type,
                title: panel.title,
                x: panel.x,
                y: panel.y,
                w: panel.w,
                h: panel.h,
                config: { ...panel.config },
                data: panel.data ? { ...panel.data } : undefined,
                _dataLoaded: panel._dataLoaded,
              });
            });
          }
        }}
        isEditable={isEditable}
        bordered={isEditable || !isListView}
      />

      <AddPanelModal
        isOpen={showAddPanel}
        onClose={() => setShowAddPanel(false)}
        onSelect={handleAddPanelSelect}
      />

      {showSettings && dashboard && (
        <DashboardSettings
          dashboard={dashboard}
          isDirty={isDirty}
          contextKey={contextKey}
          onSave={handleSettingsSave}
          onDeleteSpecific={handleDeleteSpecific}
          onClose={() => {
            setShowSettings(false);
            setConditionalFormError(null);
          }}
          conditionalFormError={conditionalFormError}
        />
      )}

      {showEditor && editingPanel && (
        <PanelEditor
          panel={editingPanel}
          contextKey={contextKey}
          layout={dashboard.layout}
          onApply={handleEditorApply}
          onCancel={handleEditorCancel}
          sourceKey={dashboard.sourceKey}
        />
      )}

      {showDeleteConfirm && (
        <div className="modal d-block" data-testid="delete-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-body">
                <p>{t('dashboard.confirmDelete')}</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" data-testid="delete-cancel-btn" onClick={cancelDelete}>
                  {t('dashboard.cancel')}
                </button>
                <button className="btn btn-danger" data-testid="delete-confirm-btn" onClick={confirmDelete}>
                  {t('dashboard.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
